import React, { useState, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Breadcrumb from '../../components/common/Breadcrumb';
import toast from 'react-hot-toast';
import {
    IconUpload, IconCheck, IconLoader, IconChevronLeft,
    IconLock, IconUsers, IconBuilding, IconAlertTriangle, IconUserCircle,
} from '@tabler/icons-react';
import { useUploadDocument, useDocument, useUpdateDocument } from '../../hooks/useDocuments';
import { useEmployee } from '../../hooks/useEmployees';
import { useAuth } from '../../contexts/AuthContext';

const CATEGORIES = [
    { value: 'cv_resume',    label: 'CV / Résumé'                  },
    { value: 'cover_letter', label: 'Cover Letter'                  },
    { value: 'certificate',  label: 'Certificate / Qualification'   },
    { value: 'id_document',  label: 'ID Document'                   },
    { value: 'contract',     label: 'Employment Contract'           },
    { value: 'appraisal',    label: 'Appraisal / Review'            },
    { value: 'training',     label: 'Training Record'               },
    { value: 'office_memo',  label: 'Office Memo / Correspondence'  },
    { value: 'policy',       label: 'Policy / Compliance Document'  },
    { value: 'personal',     label: 'Personal'                      },
    { value: 'other',        label: 'Other'                         },
];

const VISIBILITY = [
    { value: 'private',      label: 'Private',       desc: 'Only you + HR',                icon: IconLock     },
    { value: 'line_manager', label: 'Line Manager',  desc: 'You + your line manager + HR', icon: IconUsers    },
    { value: 'org',          label: 'Organisation',  desc: 'All HR in the organisation',   icon: IconBuilding },
];

const DELETION_STRATEGIES = [
    { value: 'retain',   label: 'Retain',   desc: 'Keep file archived forever'       },
    { value: 'purge',    label: 'Purge',    desc: 'Delete after retention period'    },
    { value: 'transfer', label: 'Transfer', desc: 'Move to HR archive on departure'  },
];

const ALLOWED_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'image/jpeg', 'image/png', 'image/webp',
    'text/plain', 'text/csv', 'application/zip',
];

const MAX_SIZE_MB = 20;

function formatSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Shared form used by both Upload and Edit ─────────────────────────────────

function DocumentForm({ initial = {}, isEdit = false, onSubmit, busy }) {
    const fileRef = useRef();
    const [file, setFile]           = useState(null);
    const [fileError, setFileError] = useState('');
    const [form, setForm]           = useState({
        title:             initial.title             || '',
        category:          initial.category          || 'other',
        description:       initial.description       || '',
        visibility:        initial.visibility        || 'private',
        expires_on:        initial.expires_on        || '',
        deletion_strategy: initial.deletion_strategy || 'retain',
        retention_days:    initial.retention_days    || 2555,
        tags:              (initial.tags || []).join(', '),
    });

    const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

    const handleFile = (e) => {
        const f = e.target.files[0];
        if (!f) return;
        if (!ALLOWED_TYPES.includes(f.type)) { setFileError('File type not allowed.'); return; }
        if (f.size > MAX_SIZE_MB * 1024 * 1024) { setFileError(`File exceeds ${MAX_SIZE_MB}MB limit.`); return; }
        setFileError('');
        setFile(f);
        if (!form.title) set('title', f.name.replace(/\.[^.]+$/, '').slice(0, 100));
    };

    const handleSubmit = () => {
        if (!isEdit && !file) { toast.error('Please select a file.'); return; }
        onSubmit({ file, form });
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-20">

            {/* Left col — file + basic info */}
            <div className="lg:col-span-2 flex flex-col gap-15">

                {/* File picker */}
                {!isEdit && (
                    <div className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden">
                        <div className="px-20 py-12 border-b border-border bg-bg-light/30">
                            <h3 className="text-[12px] font-bold uppercase tracking-widest text-text-primary">File</h3>
                            <p className="text-[10px] text-text-secondary mt-1">Max {MAX_SIZE_MB}MB · PDF, Word, Excel, PPT, Images, CSV, ZIP</p>
                        </div>
                        <div className="p-20">
                            <div
                                onClick={() => fileRef.current.click()}
                                className={`border-2 border-dashed rounded-lg p-30 text-center cursor-pointer transition-colors ${
                                    file ? 'border-success/40 bg-success/5' : 'border-border hover:border-primary/40 hover:bg-primary/5'
                                }`}
                            >
                                {file ? (
                                    <div className="flex flex-col items-center gap-6">
                                        <div className="w-40 h-40 rounded-full bg-success/10 flex items-center justify-center">
                                            <IconCheck size={18} className="text-success" />
                                        </div>
                                        <p className="text-[13px] font-semibold text-text-primary">{file.name}</p>
                                        <p className="text-[11px] text-text-secondary">{formatSize(file.size)}</p>
                                        <button
                                            onClick={e => { e.stopPropagation(); setFile(null); }}
                                            className="text-[10px] text-danger underline"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center gap-8">
                                        <div className="w-44 h-44 rounded-full bg-bg-light flex items-center justify-center">
                                            <IconUpload size={20} className="text-text-secondary" />
                                        </div>
                                        <p className="text-[13px] font-semibold text-text-primary">Click to choose a file</p>
                                        <p className="text-[11px] text-text-secondary">or drag and drop here</p>
                                    </div>
                                )}
                            </div>
                            <input ref={fileRef} type="file" className="hidden" onChange={handleFile} />
                            {fileError && <p className="text-[11px] text-danger mt-6">{fileError}</p>}
                        </div>
                    </div>
                )}

                {/* Basic info */}
                <div className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden">
                    <div className="px-20 py-12 border-b border-border bg-bg-light/30">
                        <h3 className="text-[12px] font-bold uppercase tracking-widest text-text-primary">Document Details</h3>
                    </div>
                    <div className="p-20 flex flex-col gap-15">
                        <div className="form-group">
                            <label className="block text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-6">Title</label>
                            <input
                                type="text"
                                placeholder="e.g. My CV 2025"
                                maxLength={100}
                                value={form.title}
                                onChange={e => set('title', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label className="block text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-6">Category</label>
                            <select value={form.category} onChange={e => set('category', e.target.value)}>
                                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="block text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-6">
                                Description <span className="normal-case font-normal text-text-secondary">(optional)</span>
                            </label>
                            <textarea
                                rows={3}
                                placeholder="Brief description of the document..."
                                value={form.description}
                                onChange={e => set('description', e.target.value)}
                                style={{ resize: 'none' }}
                            />
                        </div>
                        <div className="form-group">
                            <label className="block text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-6">
                                Tags <span className="normal-case font-normal text-text-secondary">(comma separated)</span>
                            </label>
                            <input
                                type="text"
                                placeholder="e.g. 2025, performance, nysc"
                                value={form.tags}
                                onChange={e => set('tags', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label className="block text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-6">
                                Expiry Date <span className="normal-case font-normal text-text-secondary">(optional — for certs, contracts)</span>
                            </label>
                            <input
                                type="date"
                                value={form.expires_on}
                                onChange={e => set('expires_on', e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {isEdit && (
                    <div className="flex items-start gap-10 px-15 py-12 bg-warning/5 border border-warning/20 rounded-lg">
                        <IconAlertTriangle size={14} className="text-warning flex-shrink-0 mt-0.5" />
                        <p className="text-[11px] text-text-secondary leading-relaxed">
                            To replace the file, delete this document and upload a new one.
                        </p>
                    </div>
                )}
            </div>

            {/* Right col — visibility + deletion */}
            <div className="flex flex-col gap-15">

                {/* Visibility */}
                <div className="bg-bg-dark rounded-lg border border-success/30 shadow-light overflow-hidden">
                    <div className="px-20 py-12 border-b border-success/20 bg-success/5">
                        <h3 className="text-[12px] font-bold uppercase tracking-widest text-success">Visibility</h3>
                        <p className="text-[10px] text-text-secondary mt-1">Who can see this document</p>
                    </div>
                    <div className="p-15 flex flex-col gap-8">
                        {VISIBILITY.map(v => {
                            const Icon = v.icon;
                            const active = form.visibility === v.value;
                            return (
                                <label key={v.value} className={`flex items-center gap-10 px-12 py-10 rounded-lg border cursor-pointer transition-colors ${
                                    active ? 'border-success bg-success/5' : 'border-border hover:border-success/30'
                                }`}>
                                    <input type="radio" className="hidden" value={v.value} checked={active} onChange={() => set('visibility', v.value)} />
                                    <Icon size={15} className={active ? 'text-success' : 'text-text-secondary'} />
                                    <div className="flex-grow">
                                        <p className="text-[12px] font-semibold text-text-primary">{v.label}</p>
                                        <p className="text-[10px] text-text-secondary mt-1">{v.desc}</p>
                                    </div>
                                    {active && <IconCheck size={13} className="text-success flex-shrink-0" />}
                                </label>
                            );
                        })}
                    </div>
                </div>

                {/* Deletion strategy */}
                <div className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden">
                    <div className="px-20 py-12 border-b border-border bg-bg-light/30">
                        <h3 className="text-[12px] font-bold uppercase tracking-widest text-text-primary">When I Leave</h3>
                        <p className="text-[10px] text-text-secondary mt-1">What happens to this file on departure</p>
                    </div>
                    <div className="p-15 flex flex-col gap-8">
                        {DELETION_STRATEGIES.map(d => {
                            const active = form.deletion_strategy === d.value;
                            return (
                                <label key={d.value} className={`flex items-start gap-10 px-12 py-10 rounded-lg border cursor-pointer transition-colors ${
                                    active ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'
                                }`}>
                                    <input type="radio" className="hidden" value={d.value} checked={active} onChange={() => set('deletion_strategy', d.value)} />
                                    <div className={`w-[14px] h-[14px] rounded-full border-2 flex-shrink-0 mt-0.5 transition-colors ${active ? 'border-primary bg-primary' : 'border-border'}`} />
                                    <div className="flex-grow">
                                        <p className="text-[12px] font-semibold text-text-primary">{d.label}</p>
                                        <p className="text-[10px] text-text-secondary mt-1">{d.desc}</p>
                                    </div>
                                </label>
                            );
                        })}
                        {form.deletion_strategy === 'purge' && (
                            <div className="form-group mt-4">
                                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-6">Retain for (days)</label>
                                <input
                                    type="number"
                                    min={1}
                                    value={form.retention_days}
                                    onChange={e => set('retention_days', e.target.value)}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Submit */}
                <button onClick={handleSubmit} disabled={busy} className="btn btn-primary w-full justify-center">
                    {busy
                        ? <IconLoader className="animate-spin" size={15} />
                        : isEdit ? <IconCheck size={15} /> : <IconUpload size={15} />
                    }
                    {isEdit ? 'Save Changes' : 'Upload Document'}
                </button>
            </div>
        </div>
    );
}

// ── Upload Page ──────────────────────────────────────────────────────────────

export function UploadDocumentPage() {
    const navigate    = useNavigate();
    const queryClient = useQueryClient();
    const uploadDoc   = useUploadDocument();
    const { hasAnyPermission, user } = useAuth();
    const isHR        = !!user?.is_superuser || hasAnyPermission(['rbacapp.hr_view', 'rbacapp.hr_act', 'rbacapp.hr_approve']);
    const [searchParams] = useSearchParams();
    const employeeId  = isHR ? searchParams.get('employee_id') : null;
    const { data: onBehalfEmployee } = useEmployee(employeeId, user?.orgId);

    const backLink = employeeId ? `/employee/${employeeId}/snapshot` : '/documents';

    const handleSubmit = async ({ file, form }) => {
        const fd = new FormData();
        fd.append('file', file);
        if (form.title)       fd.append('title', form.title);
        fd.append('category',          form.category);
        if (form.description) fd.append('description', form.description);
        fd.append('visibility',        form.visibility);
        if (form.expires_on)  fd.append('expires_on', form.expires_on);
        fd.append('deletion_strategy', form.deletion_strategy);
        fd.append('retention_days',    String(form.retention_days));
        const tags = form.tags.split(',').map(t => t.trim()).filter(Boolean);
        if (tags.length)      fd.append('tags', JSON.stringify(tags));
        if (employeeId)       fd.append('employee_id', employeeId);

        try {
            await uploadDoc.mutateAsync(fd);
            if (employeeId) {
                queryClient.invalidateQueries({ queryKey: ['employee-snapshot', employeeId] });
            }
            toast.success('Document uploaded successfully.');
            navigate(backLink, { replace: true });
        } catch (err) {
            const d = err?.response?.data;
            if (d?.file) toast.error(d.file[0]);
        }
    };

    return (
        <div className="flex flex-col gap-20 pb-30">
            <Breadcrumb title="Upload Document" path="Documents" />
            <button onClick={() => navigate(backLink)} className="btn btn-outline-secondary self-start">
                <IconChevronLeft size={15} /> Back
            </button>
            {employeeId && (
                <div className="flex items-center gap-10 px-15 py-10 bg-info/10 border border-info/30 rounded-lg">
                    <IconUserCircle size={16} className="text-info flex-shrink-0" />
                    <p className="text-[12px] text-text-primary">
                        Uploading on behalf of <span className="font-bold">{onBehalfEmployee?.full_name || 'this employee'}</span>.
                    </p>
                </div>
            )}
            <DocumentForm onSubmit={handleSubmit} busy={uploadDoc.isPending} />
        </div>
    );
}

// ── Edit Page ────────────────────────────────────────────────────────────────

export function EditDocumentPage() {
    const { id }      = useParams();
    const navigate    = useNavigate();
    const updateDoc   = useUpdateDocument();
    const { data: doc, isLoading } = useDocument(id);

    if (isLoading) return (
        <div className="flex items-center justify-center py-60 text-text-secondary">
            <IconLoader className="animate-spin" size={22} />
        </div>
    );

    const handleSubmit = async ({ form }) => {
        try {
            const fd = new FormData();
            fd.append('title',             form.title);
            fd.append('category',          form.category);
            fd.append('description',       form.description);
            fd.append('visibility',        form.visibility);
            fd.append('expires_on',        form.expires_on || '');
            fd.append('deletion_strategy', form.deletion_strategy);
            fd.append('retention_days',    String(form.retention_days));
            const tags = form.tags.split(',').map(t => t.trim()).filter(Boolean);
            fd.append('tags', JSON.stringify(tags));

            await updateDoc.mutateAsync({ id, data: fd });
            toast.success('Document updated.');
            navigate(-1);
        } catch {}
    };

    return (
        <div className="flex flex-col gap-20 pb-30">
            <Breadcrumb title="Edit Document" path="Documents" />
            <button onClick={() => navigate(-1)} className="btn btn-outline-secondary self-start">
                <IconChevronLeft size={15} /> Back
            </button>
            {doc && (
                <DocumentForm
                    initial={doc}
                    isEdit
                    onSubmit={handleSubmit}
                    busy={updateDoc.isPending}
                />
            )}
        </div>
    );
}
